import 'firebase/compat/auth'

import {Box, Flex, Text, Title} from 'exsportia-components'
import firebaseRaw from 'firebase/compat/app'
import {ChangeEvent, FC, useEffect, useState} from 'react'
import {useQueryClient} from 'react-query'

import {changePhoneApi, checkPhoneForChangeApi} from '../../api/user/phone-api'
import {Button} from '../../ui/components/button/button'
import {Input} from '../../ui/components/input/input'
import {PhoneInput} from '../../ui/components/phone-input/phone-input'
import {handleBackendError} from '../../utils/error-handler'
import {auth} from '../../utils/firebase'
import {useLocale} from '../../utils/i18n'

const RESEND_TIMER_INITIAL = 60

type CustomWindow = Window & {
  recaptchaVerifier?: firebaseRaw.auth.RecaptchaVerifier
}

export type ChangePhoneFlowProps = {
  currentPhone: string
  phoneClaimed?: boolean
  viewOnly?: boolean
}

export const ChangePhoneFlow: FC<ChangePhoneFlowProps> = ({currentPhone, phoneClaimed, viewOnly}) => {
  const {l} = useLocale()
  const queryClient = useQueryClient()

  if (viewOnly) {
    return (
      <Flex flexDirection='column' gap='4px'>
        <Text text={l('phoneNumber')} settings={{color: 'text-secondary', fontSize: '14px'}} />
        <Text text={currentPhone} settings={{fontWeight: 'bold', fontSize: '16px'}} />
      </Flex>
    )
  }

  // Steps: 'phone' => initial, 'otp' => waiting for code
  const [step, setStep] = useState<'otp' | 'phone'>('phone')
  const [isEditing, setIsEditing] = useState(false)
  const [newPhone, setNewPhone] = useState(!phoneClaimed ? currentPhone : '')
  const [otp, setOtp] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  useEffect(() => {
    if (currentPhone && !newPhone && !phoneClaimed) {
      setNewPhone(currentPhone)
    }
  }, [currentPhone, phoneClaimed])

  const [isLoading, setIsLoading] = useState(false)
  const [timer, setTimer] = useState(0)

  const [confirmationResult, setConfirmationResult] = useState<firebaseRaw.auth.ConfirmationResult | null>(null)

  const maskedPhone = currentPhone.length > 4
    ? `${currentPhone.slice(0, 4)} ******${currentPhone.slice(-2)}`
    : currentPhone

  useEffect(() => {
    return () => {
      const verifier = (window as CustomWindow).recaptchaVerifier

      if (verifier?.clear) {
        verifier.clear()
      }

      delete (window as CustomWindow).recaptchaVerifier
    }
  }, [])

  useEffect(() => {
    let interval: NodeJS.Timeout
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1)
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [timer])

  const setupRecaptcha = () => {
    if ((window as CustomWindow).recaptchaVerifier) {
      return
    }
    try {
      (window as CustomWindow).recaptchaVerifier = new firebaseRaw.auth.RecaptchaVerifier('recaptcha-change-phone', {
        size: 'invisible',
      })
    } catch (e) {
      console.warn('Recaptcha init warn', e)
    }
  }

  const handleSendOtp = async (isResend = false) => {
    setValidationError(null)
    if (!newPhone || newPhone.length < 5) {
      setValidationError('Please enter a valid phone number.')
      return
    }
    setIsLoading(true)

    try {
      // 1. Pre-validation via backend
      if (!isResend) {
        await checkPhoneForChangeApi({phone: newPhone})
      }

      // 2. Trigger Firebase OTP
      setupRecaptcha()
      const appVerifier = (window as CustomWindow).recaptchaVerifier
      if (!appVerifier) {
        throw new Error('Recaptcha verifier not initialized')
      }
      const result = await auth.signInWithPhoneNumber(newPhone.startsWith('+') ? newPhone : `+${newPhone}`, appVerifier)
      setConfirmationResult(result)

      setStep('otp')
      setTimer(RESEND_TIMER_INITIAL)
    } catch (err: unknown) {
      const error = err as { response?: { data?: { code?: string } } }
      const apiCode = error.response?.data?.code
      if (apiCode === 'PHONE_TAKEN') {
        setValidationError('This number is already associated with another account.')
      } else if (apiCode) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        handleBackendError(err as any, l)
      } else {
        // Firebase error
        setValidationError('Failed to verify phone. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 6 || !confirmationResult) {
      return
    }
    setIsLoading(true)
    setValidationError(null)

    try {
      // 1. Capture original user's token BEFORE confirming OTP
      //    (confirming OTP changes auth.currentUser to the phone-auth user)
      const originalToken = await auth.currentUser?.getIdToken(true)

      // 2. Confirm OTP with Firebase
      const result = await confirmationResult.confirm(otp)

      // 3. Get phone-auth ID token
      const phoneIdToken = await result.user?.getIdToken()
      if (!phoneIdToken) {
        throw new Error('No id_token returned')
      }

      // 4. Final API Call — phoneIdToken in body, originalToken in Authorization
      const response = await changePhoneApi(phoneIdToken, originalToken)

      // 5. If server returned a token, sign in with it to refresh the user state
      if (response.token) {
        await auth.signInWithCustomToken(response.token)
      }

      // 6. Success - Invalidate with the correct key (capital G)
      await queryClient.invalidateQueries(['GetUserQuery'])
      setStep('phone')
      setOtp('')
      setNewPhone('')
    } catch (err: unknown) {
      const error = err as { response?: { data?: { code?: string } } }
      if (error.response?.data?.code) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        handleBackendError(err as any, l)
      } else {
        setValidationError('Failed to verify phone. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (step === 'phone') {
    return (
      <Flex flexDirection='column' gap='16px'>
        <div id='recaptcha-change-phone'></div>

        {/* Current Phone Section - Always visible */}
        <Box>
          <Text text={l('current_phone', {defaultValue: 'Current Phone'})} settings={{color: 'text-secondary', fontSize: '14px'}} />
          <Flex alignItems='center' justifyContent='space-between' gap='8px' mt='4px'>
            <Text text={currentPhone ? (phoneClaimed ? maskedPhone : currentPhone) : '-'} settings={{fontWeight: 'bold', fontSize: '16px'}} />
            {!isEditing && (
              <Box>
                <Button
                  type='icon'
                  icon='edit'
                  onClick={() => {
                    setNewPhone(currentPhone || '')
                    setIsEditing(true)
                  }}
                />
              </Box>
            )}
          </Flex>
        </Box>

        {/* Edit Section - Visible only when editing */}
        {isEditing && (
          <Box>
            <Box>
              <PhoneInput
                id='change-phone-input'
                label={phoneClaimed ? l('new_phone_number', {defaultValue: 'New Phone Number'}) : l('phoneNumber')}
                value={newPhone}
                onChange={(val) => {
                  setNewPhone(val)
                  setValidationError(null)
                }}
                disabled={isLoading}
              />
              {validationError && (
                <Box mt='5px' fontSize='12px' color='error'>
                  {validationError}
                </Box>
              )}
            </Box>
            <Flex justifyContent='flex-end' gap='8px' mt='16px'>
              <Box>
                <Button type='flat' onClick={() => setIsEditing(false)} disabled={isLoading} settings={{width: 'max-content'}}>
                  {l('cancel', {defaultValue: 'Cancel'})}
                </Button>
              </Box>
              <Box>
                <Button
                  onClick={() => {
                    void handleSendOtp(false)
                  }}
                  disabled={
                    isLoading ||
                    !newPhone ||
                    (phoneClaimed && newPhone.replace('+', '') === currentPhone.replace('+', ''))
                  }
                  settings={{width: 'max-content'}}
                >
                  {phoneClaimed ? l('update', {defaultValue: 'Update'}) : l('claim', {defaultValue: 'Claim'})}
                </Button>
              </Box>
            </Flex>
          </Box>
        )}
      </Flex>
    )
  }

  return (
    <Box>
      <Title text={l('enter_otp')} styleType='h3' />
      <Box mt='8px' mb='16px'>
        <Text text={l('enter_otp_description')} />
      </Box>
      <Input
        id='change-phone-otp'
        label='OTP'
        placeholder='XXXXXX'
        value={otp}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
          setValidationError(null)
        }}
        disabled={isLoading}
      />
      {validationError && (
        <Box mt='5px' fontSize='12px' color='error'>
          {validationError}
        </Box>
      )}
      <Flex mt='16px' justifyContent='center'>
        <Button
          type='flat'
          disabled={timer > 0 || isLoading}
          onClick={() => {
            void handleSendOtp(true)
          }}
        >
          {timer > 0 ? l('resend_otp_in', {seconds: timer}) : l('resend_otp')}
        </Button>
      </Flex>
      <Flex mt='24px' gap='16px' justifyContent='flex-end'>
        <Button type='secondary' onClick={() => setStep('phone')} disabled={isLoading}>
          {l('back')}
        </Button>
        <Button
          onClick={() => {
            void handleVerifyOtp()
          }}
          disabled={isLoading || otp.length < 6}
        >
          {l('verify')}
        </Button>
      </Flex>
    </Box>
  )
}
